import { getAnalyticsPageUrl } from '@/lib/analyticsPageUrl';

export type PagePromptsAnswer = { status: 'none' } | { status: 'pending' } | { prompts: string[]; status: 'ready' };

// One request answers every launcher on the page; a launcher it leaves out has no answer.
export type LauncherPrompts = Readonly<Record<string, string[]>>;

export interface PagePromptsReader {
  // Pending is the page's; once settled, each launcher has its own answer, none for one the request left out.
  current(launcherId: string): PagePromptsAnswer;
  // Fires on every transition; the listener reads current(id) for the page it is on now.
  subscribe(listener: () => void): () => void;
  whenSettled(boundMs: number): Promise<void>;
}

export interface PagePromptsRequest {
  readonly pageIdentity: string;
}

export interface PagePromptsHolder extends PagePromptsReader {
  arm(): void;
  begin(pageIdentity: string): PagePromptsRequest;
  settle(request: PagePromptsRequest, prompts: LauncherPrompts | null): void;
  disarm(): void;
}

type State =
  | { kind: 'idle' }
  | { kind: 'armed' }
  | { kind: 'in-flight'; request: PagePromptsRequest }
  | { answers: ReadonlyMap<string, PagePromptsAnswer>; kind: 'settled'; pageIdentity: string };

interface Waiter {
  pageIdentity: string;
  resolve: () => void;
}

// Shared instances, and each launcher's ready answer built once at settle: a subscriber compares snapshots by reference.
const NONE: PagePromptsAnswer = { status: 'none' };
const PENDING: PagePromptsAnswer = { status: 'pending' };

export function createPagePromptsHolder(): PagePromptsHolder {
  let state: State = { kind: 'idle' };
  let waiters: Waiter[] = [];
  let listeners: Array<() => void> = [];

  function pendingFor(pageIdentity: string): boolean {
    // Armed names no page yet: the tracker's chunk is still loading, so every page is pending.
    return state.kind === 'armed' || (state.kind === 'in-flight' && state.request.pageIdentity === pageIdentity);
  }

  function transition(next: State): void {
    state = next;
    const done = waiters.filter((waiter) => !pendingFor(waiter.pageIdentity));
    waiters = waiters.filter((waiter) => pendingFor(waiter.pageIdentity));
    done.forEach((waiter) => waiter.resolve());
    // A copy, so a listener that unsubscribes itself does not skip its neighbour.
    [...listeners].forEach((listener) => listener());
  }

  return {
    arm() {
      transition({ kind: 'armed' });
    },
    begin(pageIdentity) {
      const request: PagePromptsRequest = { pageIdentity };
      transition({ kind: 'in-flight', request });
      return request;
    },
    settle(request, prompts) {
      // Matched by request, not by page: an earlier request for the same page can answer late.
      if (state.kind !== 'in-flight' || state.request !== request) return;
      const answers = new Map<string, PagePromptsAnswer>(
        Object.entries(prompts ?? {}).map(([launcherId, launcherPrompts]) => [
          launcherId,
          { prompts: launcherPrompts, status: 'ready' },
        ]),
      );
      transition({ answers, kind: 'settled', pageIdentity: request.pageIdentity });
    },
    disarm() {
      transition({ kind: 'idle' });
    },
    current(launcherId) {
      const pageIdentity = getAnalyticsPageUrl();
      if (pendingFor(pageIdentity)) return PENDING;
      if (state.kind === 'settled' && state.pageIdentity === pageIdentity) return state.answers.get(launcherId) ?? NONE;
      return NONE;
    },
    subscribe(listener) {
      listeners.push(listener);
      return () => {
        listeners = listeners.filter((other) => other !== listener);
      };
    },
    whenSettled(boundMs) {
      const pageIdentity = getAnalyticsPageUrl();
      if (!pendingFor(pageIdentity)) return Promise.resolve();
      return new Promise((resolve) => {
        const waiter: Waiter = {
          pageIdentity,
          resolve: () => {
            clearTimeout(bound);
            resolve();
          },
        };
        const bound = setTimeout(() => {
          waiters = waiters.filter((other) => other !== waiter);
          resolve();
        }, boundMs);
        waiters.push(waiter);
      });
    },
  };
}

// A bar with no AI source names no launcher to read for.
export function answerFor(reader: PagePromptsReader, launcherId: string | null): PagePromptsAnswer {
  return launcherId === null ? NONE : reader.current(launcherId);
}
